import type { ForecastContextSelectedOptions } from "@appretio/context-selector";
import type { MutableRefObject, RefObject } from "react";
import { useCallback } from "react";
import type { CubeWindow } from "../../components/cubeDataTable/utils/columnUtils";
import { type IntakeCaches } from "../../components/cubeDataTable/utils/intakeUtils";
import { mergeStoreLocations } from "../../utils/mergeStoreLocations";
import type {
  FetchForecastDataParams,
  ForecastGridDataOutput
} from "../useFetchForecastData";
import type { WorkspaceTableState } from "./useWorkspaceTableState";

/** Inputs for {@link useHandleApply}. */
interface UseHandleApplyOptions {
  /** Flat list of cube identifiers currently selected. */
  cubeIds: string[];
  /** The writable own cube among ``cubeIds`` (undefined when none). */
  saveCubeId: string | undefined;
  /** That cube's week window, as the versions payload ships it. */
  saveCubeWindow?: CubeWindow;
  /** Whether that cube is frozen (budget only; always false for user cubes). */
  isFrozen?: boolean;
  /** Currently selected (unapplied) context options. */
  selectedContextOptions: ForecastContextSelectedOptions;
  /** Store-status-resolved locations to merge into context on Apply. */
  resolvedStoreLocations: string[];
  /** Imperative forecast-data fetcher. */
  fetchForecastData: (
    params: FetchForecastDataParams
  ) => Promise<ForecastGridDataOutput | null>;
  /** Ref holding one intake cache per source not on the grid. */
  intakeCacheRef: MutableRefObject<IntakeCaches>;
  /** True while the workspace is mounted (``useRetainedForecastData``). */
  mountedRef: RefObject<boolean>;
  /** Table state setters owned by ``useWorkspaceTableState``. */
  tableState: WorkspaceTableState;
}

/**
 * Memoised Apply handler extracted from ``useForecastWorkspace``.
 *
 * Lives in its own hook so the parent stays under the max-lines-per-fn
 * lint budget and the dependency list for this callback is visible in
 * one place — a sibling hook that diverges is easier to spot than a
 * single mega-``useCallback`` buried deeper in the parent.
 */
export function useHandleApply({
  cubeIds,
  saveCubeId,
  saveCubeWindow,
  isFrozen,
  selectedContextOptions,
  resolvedStoreLocations,
  fetchForecastData,
  intakeCacheRef,
  mountedRef,
  tableState
}: UseHandleApplyOptions) {
  const {
    setHasVersionError,
    setAppliedFilters,
    setTableControls,
    setSavedAt
  } = tableState;

  return useCallback(async () => {
    if (cubeIds.length === 0) {
      setHasVersionError(true);
      return;
    }
    setHasVersionError(false);
    const context = mergeStoreLocations(
      selectedContextOptions ?? {},
      resolvedStoreLocations
    );
    try {
      const data = await fetchForecastData({ context, cubeIds, saveCubeId });
      setAppliedFilters({
        context,
        cubeIds,
        saveCubeId,
        saveCubeWindow,
        isFrozen
      });
      // A load opens on the source its rows carry: the tenant's default.
      if (data) {
        setTableControls((prev) => ({
          ...prev,
          intake: data.main_intake_source
        }));
      }
      // The save indicator described the rows the fetch replaced.
      setSavedAt(null);
      // Retained, so not rolled back with the rest when unmounted.
      if (mountedRef.current) {
        intakeCacheRef.current = {};
      }
    } catch {
      // The error is surfaced via the global MutationCache toast.
    }
  }, [
    cubeIds,
    saveCubeId,
    saveCubeWindow,
    isFrozen,
    selectedContextOptions,
    resolvedStoreLocations,
    fetchForecastData,
    setHasVersionError,
    setAppliedFilters,
    setTableControls,
    setSavedAt,
    intakeCacheRef,
    mountedRef
  ]);
}
